import React, {
    createContext,
    useState,
    useContext,
    useCallback,
    useRef,
    ReactNode,
    useEffect,
} from 'react';
import { useMap } from 'rwl-ui';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models';
import { useGlobal } from './useGlobal';
import { useServiceAreasList } from '../fetchers/MapFetcher/useServiceAreasList';
import { filterServiceAreas } from '../utils/serviceAreaUtils';
import {
    adjustMapViewport,
    BASE_ZOOM,
    Cluster,
    clusterServiceAreas,
} from '../utils/mapUtils';
import { usePostCode } from './usePostCode';
import { useNeighborhood } from '../fetchers/MapFetcher/useNeighborhood';

export enum NeighborhoodStatus {
    NOT_SET = 'NOT_SET',
    FOUND = 'FOUND',
    NOT_FOUND = 'NOT_FOUND',
}

interface MapContextProps {
    neighborhood: NeighborhoodServiceAreaInfo | undefined;
    setNeighborhood: React.Dispatch<
        React.SetStateAction<NeighborhoodServiceAreaInfo | undefined>
    >;
    serviceAreas: NeighborhoodServiceAreaInfo[];
    setServiceAreas: React.Dispatch<
        React.SetStateAction<NeighborhoodServiceAreaInfo[]>
    >;
    clusters: Cluster[];
    setClusters: React.Dispatch<React.SetStateAction<Cluster[]>>;
    openMarkerId: string | undefined;
    setOpenMarkerId: React.Dispatch<React.SetStateAction<string | undefined>>;
    neighborhoodStatus: NeighborhoodStatus;
    setNeighborhoodStatus: React.Dispatch<
        React.SetStateAction<NeighborhoodStatus>
    >;
    serviceAreaHoverStore: React.MutableRefObject<ServiceAreaHoverStore>;
    setHoveredServiceAreaPlaceId: (placeId: string | undefined) => void;
    markerActionsRef: React.MutableRefObject<Map<string, () => void>>;
}

export type ServiceAreaHoverStore = {
    placeId: string | undefined;
    listeners: Set<() => void>;
};

const MapContext = createContext<MapContextProps | undefined>(undefined);

interface MapProviderProps {
    children: ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
    const { accountId, config } = useGlobal();
    const { map, isInitialized } = useMap();
    const { data: serviceAreaInfo } = useServiceAreasList({ accountId });
    const postCode = usePostCode();
    const { data: fetchedNeighborhood, isFetched } = useNeighborhood({
        accountId,
        postCode,
    });

    const [neighborhood, setNeighborhood] = useState<
        NeighborhoodServiceAreaInfo | undefined
    >(undefined);
    const [serviceAreas, setServiceAreas] = useState<
        NeighborhoodServiceAreaInfo[]
    >([]);
    const [clusters, setClusters] = useState<Cluster[]>([]);
    const [openMarkerId, setOpenMarkerId] = useState<string>();
    const [neighborhoodStatus, setNeighborhoodStatus] =
        useState<NeighborhoodStatus>(NeighborhoodStatus.NOT_SET);
    const serviceAreaHoverStore = useRef<ServiceAreaHoverStore>({
        placeId: undefined,
        listeners: new Set(),
    });
    const markerActionsRef = useRef<Map<string, () => void>>(new Map());

    const setHoveredServiceAreaPlaceId = useCallback(
        (placeId: string | undefined) => {
            const store = serviceAreaHoverStore.current;
            if (store.placeId === placeId) return;
            store.placeId = placeId;
            store.listeners.forEach((l) => l());
        },
        []
    );

    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('zoom_changed', () => {
            const newZoom = map.getZoom() || BASE_ZOOM;
            const clustered = clusterServiceAreas(serviceAreas, newZoom);
            setClusters(clustered);
        });

        const initialZoom = map.getZoom() || BASE_ZOOM;
        const initialClusters = clusterServiceAreas(serviceAreas, initialZoom);
        setClusters(initialClusters);

        return () => {
            google.maps.event.removeListener(listener);
        };
    }, [map, serviceAreas]);

    useEffect(() => {
        if (!neighborhood && serviceAreaInfo && isInitialized && map) {
            const filteredServiceAreas = filterServiceAreas(
                serviceAreaInfo,
                config?.account?.geolocation
            );

            setServiceAreas(filteredServiceAreas);

            const boundsArray = filteredServiceAreas
                .map((serviceArea) => serviceArea.placeInfo?.geometry?.viewport)
                .filter(
                    (
                        viewport
                    ): viewport is {
                        northeast: { lat: number; lng: number };
                        southwest: { lat: number; lng: number };
                    } => Boolean(viewport)
                )
                .map((viewport) => ({
                    north: viewport.northeast.lat,
                    east: viewport.northeast.lng,
                    south: viewport.southwest.lat,
                    west: viewport.southwest.lng,
                }));

            if (boundsArray.length) {
                adjustMapViewport(map, boundsArray);
            }
        }
    }, [
        serviceAreaInfo,
        isInitialized,
        map,
        config?.account?.geolocation,
        neighborhood,
    ]);

    // When a postcode is selected, set the neighborhood
    useEffect(() => {
        if (postCode && serviceAreaInfo && fetchedNeighborhood) {
            setNeighborhood(fetchedNeighborhood);
            setNeighborhoodStatus(NeighborhoodStatus.FOUND);
        } else if (postCode && !fetchedNeighborhood && isFetched) {
            setNeighborhoodStatus(NeighborhoodStatus.NOT_FOUND);
        }
    }, [postCode, serviceAreaInfo, fetchedNeighborhood, isFetched]);

    useEffect(() => {
        if (fetchedNeighborhood && isInitialized && map) {
            // Adjust the map viewport
            const viewport = fetchedNeighborhood.placeInfo?.geometry?.viewport;
            if (!viewport) return;
            const bounds = {
                north: viewport.northeast.lat,
                east: viewport.northeast.lng,
                south: viewport.southwest.lat,
                west: viewport.southwest.lng,
            };
            adjustMapViewport(map, [bounds]);
        }
    }, [fetchedNeighborhood, isInitialized, map]);

    return (
        <MapContext.Provider
            value={{
                neighborhood,
                setNeighborhood,
                serviceAreas,
                setServiceAreas,
                setClusters,
                clusters,
                openMarkerId,
                setOpenMarkerId,
                neighborhoodStatus,
                setNeighborhoodStatus,
                serviceAreaHoverStore,
                setHoveredServiceAreaPlaceId,
                markerActionsRef,
            }}
        >
            {children}
        </MapContext.Provider>
    );
};

export const useMapContext = () => {
    const context = useContext(MapContext);
    if (context === undefined) {
        throw new Error('useMapContext must be used within a MapProvider');
    }
    return context;
};
